import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizQuestions, calculateRecommendation, BASELINE_PROGRAM, type ContentResult } from '@/mocks/courseData';
import { apiFetch } from '@/lib/api';

const CURATION_SITE_CODE = 'LMS1';

/**
 * Fire-and-forget: persist this diagnosis attempt to the curation service so
 * it shows up in that service's own history/analytics. The quiz here is a
 * separate, product-specific question set from curation's own /api/questions
 * catalog, so this is a best-effort submission in curation's real request
 * shape (site_code + answers[] of {question_id, option_id} + our locally
 * computed result) — curation may reject it (400) if it can't resolve the
 * option ids against its own catalog. That's fine: failures never block the
 * UI, they're just logged. Curation is fully anonymous (no auth) so this
 * always fires, whether or not the visitor has looked up a mileage ID.
 */
async function saveRecommendationToCuration(answers: string[], result: ContentResult) {
  try {
    const response = await apiFetch('/api/curation/recommendations', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        site_code: CURATION_SITE_CODE,
        answers: quizQuestions.map((q, idx) => ({
          question_id: String(q.id),
          option_id: answers[idx],
        })),
        result: {
          content_id: result.id,
          title: result.title,
          match_percent: result.matchPercent,
        },
      }),
    });
    if (!response.ok) {
      console.warn('[course-recommend] curation save rejected', response.status);
    }
  } catch (error) {
    console.warn('[course-recommend] curation save failed', error);
  }
}

const resultImages: Record<string, string> = {
  'job-vlog': '/images/content-job-vlog.jpg',
  'live-lecture': '/images/content-lecture.jpg',
  'self-growth': '/images/content-growth.jpg',
  'ai-literacy': '/images/content-ai.jpg',
};

type Step = 'start' | 'quiz' | 'loading' | 'result';

export default function CourseRecommendPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('start');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [results, setResults] = useState<ContentResult[]>([]);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (step === 'result' && results.length > 0) {
      saveRecommendationToCuration(answers, results[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, results]);

  const handleStart = useCallback(() => {
    setStep('quiz');
    setCurrentQ(0);
    setAnswers([]);
    setSelectedOption(null);
    setResults([]);
  }, []);

  const handleSelectOption = useCallback((value: string) => {
    setSelectedOption(value);
  }, []);

  const handleNext = useCallback(() => {
    if (!selectedOption) return;

    const newAnswers = [...answers, selectedOption];
    setAnswers(newAnswers);

    if (currentQ < quizQuestions.length - 1) {
      setExiting(true);
      setTimeout(() => {
        setCurrentQ((prev) => prev + 1);
        setSelectedOption(null);
        setExiting(false);
      }, 280);
    } else {
      setExiting(true);
      setTimeout(() => {
        setStep('loading');
        setExiting(false);
        setTimeout(() => {
          const recommendation = calculateRecommendation(newAnswers);
          setResults(recommendation);
          setStep('result');
        }, 1400);
      }, 280);
    }
  }, [selectedOption, answers, currentQ]);

  const handlePrev = useCallback(() => {
    if (currentQ === 0) return;
    setExiting(true);
    setTimeout(() => {
      setCurrentQ((prev) => prev - 1);
      setAnswers((prev) => prev.slice(0, -1));
      setSelectedOption(answers[answers.length - 2] || null);
      setExiting(false);
    }, 280);
  }, [currentQ, answers]);

  const handleRestart = useCallback(() => {
    setStep('start');
    setCurrentQ(0);
    setAnswers([]);
    setSelectedOption(null);
    setResults([]);
  }, []);

  const question = quizQuestions[currentQ];
  const progress = currentQ / quizQuestions.length;

  return (
    <div className="min-h-screen bg-primary-50/40 flex flex-col items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        {step === 'start' && (
          <StartScreen onStart={handleStart} />
        )}

        {step === 'quiz' && (
          <div className={`transition-all duration-300 ${exiting ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'}`}>
            <QuizScreen
              question={question}
              currentQ={currentQ}
              totalQ={quizQuestions.length}
              selectedOption={selectedOption}
              onSelect={handleSelectOption}
              onNext={handleNext}
              onPrev={currentQ > 0 ? handlePrev : undefined}
              progress={progress}
            />
          </div>
        )}

        {step === 'loading' && (
          <LoadingScreen />
        )}

        {step === 'result' && results.length > 0 && (
          <ResultScreen results={results} onRestart={handleRestart} onGoHome={() => navigate('/')} />
        )}
      </div>
    </div>
  );
}

function StartScreen({ onStart }: { onStart: () => void }) {
  return (
    <div className="animate-slide-up text-center">
      <div className="bg-background-50 rounded-2xl border-2 border-primary-100 p-8">
        <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary-100 flex items-center justify-center">
          <i className="ri-compass-3-line text-3xl text-primary-500"></i>
        </div>
        <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">
          콘텐츠 추천 진단
        </h1>
        <p className="text-sm text-foreground-500 leading-relaxed mb-2">
          딱 6개의 질문에 답하면
        </p>
        <p className="text-sm text-foreground-500 leading-relaxed mb-6">
          나에게 딱 맞는 콘텐츠를 추천해드려요
        </p>

        <div className="flex items-center justify-center gap-1.5 mb-6">
          {quizQuestions.map((q) => (
            <div key={q.id} className="w-2 h-2 rounded-full bg-secondary-300" />
          ))}
        </div>

        <button
          type="button"
          onClick={onStart}
          className="w-full py-3.5 rounded-xl bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 active:scale-[0.98] transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          시작하기
        </button>
      </div>
    </div>
  );
}

function QuizScreen({
  question,
  currentQ,
  totalQ,
  selectedOption,
  onSelect,
  onNext,
  onPrev,
  progress,
}: {
  question: typeof quizQuestions[number];
  currentQ: number;
  totalQ: number;
  selectedOption: string | null;
  onSelect: (value: string) => void;
  onNext: () => void;
  onPrev?: () => void;
  progress: number;
}) {
  return (
    <div className="bg-background-50 rounded-2xl border-2 border-primary-100 p-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={onPrev}
          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
            onPrev ? 'hover:bg-background-100 text-foreground-500' : 'text-foreground-200 cursor-default'
          }`}
          disabled={!onPrev}
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <span className="text-xs font-medium text-foreground-400">
          {currentQ + 1} / {totalQ}
        </span>
        <div className="w-8 h-8" />
      </div>

      {/* Progress Bar */}
      <div className="w-full h-1.5 rounded-full bg-secondary-200 overflow-hidden mb-6">
        <div
          className="h-full rounded-full bg-primary-500 transition-all duration-500 ease-out"
          style={{ width: `${(currentQ / totalQ) * 100}%` }}
        />
      </div>

      {/* Question */}
      <h2 className="text-lg font-heading font-bold text-foreground-950 mb-1">
        {question.question}
      </h2>
      <p className="text-sm text-foreground-400 mb-5">
        {question.subtitle}
      </p>

      {/* Options */}
      <div className="space-y-2.5 mb-5">
        {question.options.map((option) => {
          const isSelected = selectedOption === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onSelect(option.value)}
              className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                isSelected
                  ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-100/60'
                  : 'border-background-200 hover:border-primary-200 hover:bg-background-50'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                    isSelected ? 'border-primary-500 bg-primary-500' : 'border-secondary-300'
                  }`}
                >
                  {isSelected && (
                    <i className="ri-check-line text-xs text-background-50"></i>
                  )}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${isSelected ? 'text-primary-700' : 'text-foreground-800'}`}>
                    {option.label}
                  </p>
                  <p className="text-xs text-foreground-400 mt-0.5 leading-relaxed">
                    {option.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Next Button */}
      <button
        type="button"
        disabled={!selectedOption}
        onClick={onNext}
        className={`w-full py-3.5 rounded-xl text-base font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
          selectedOption
            ? 'bg-primary-500 text-background-50 hover:bg-primary-600 active:scale-[0.98]'
            : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
        }`}
      >
        {currentQ < totalQ - 1 ? '다음' : '결과 보기'}
      </button>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="bg-background-50 rounded-2xl border-2 border-primary-100 p-8 text-center animate-slide-up">
      <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-primary-100 flex items-center justify-center">
        <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
      </div>
      <h2 className="text-lg font-heading font-bold text-foreground-950 mb-2">
        콘텐츠를 분석하고 있어요
      </h2>
      <p className="text-sm text-foreground-400 leading-relaxed">
        응답을 바탕으로 나에게 딱 맞는 콘텐츠를 찾는 중...
      </p>
    </div>
  );
}

function ResultScreen({
  results,
  onRestart,
  onGoHome,
}: {
  results: ContentResult[];
  onRestart: () => void;
  onGoHome: () => void;
}) {
  const isTie = results.length > 1;
  return (
    <div className="animate-slide-up space-y-4">
      {/* 추천 헤더 */}
      <div className="text-center">
        <p className="text-sm text-foreground-400">진단이 끝났어요!</p>
        <h2 className="text-lg font-heading font-bold text-foreground-950 mt-1">
          {isTie ? '나에게 딱 맞는 콘텐츠 2가지예요' : '나에게 딱 맞는 콘텐츠예요'}
        </h2>
      </div>

      {/* 추천 카테고리 카드(동점이면 여러 개) */}
      {results.map((result) => (
        <div key={result.id} className="bg-background-50 rounded-2xl border-2 border-primary-100 overflow-hidden">
          {/* Image */}
          <div className="relative h-40 overflow-hidden">
            <img
              src={resultImages[result.id]}
              alt={result.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
            <div className="absolute top-3 right-3 bg-background-50/90 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-1.5">
              <i className="ri-thumb-up-fill text-sm text-primary-500"></i>
              <span className="text-xs font-bold text-foreground-800">{result.matchPercent}% 매칭</span>
            </div>
          </div>

          <div className="p-5">
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-1">{result.title}</h3>
            <p className="text-sm text-primary-600 font-medium mb-3">{result.subtitle}</p>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">{result.description}</p>

            <div className="flex flex-wrap gap-1.5 mb-4">
              {result.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium border border-accent-200">
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-4 mb-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1"><i className="ri-time-line text-sm"></i>{result.duration}</span>
              <span className="flex items-center gap-1"><i className="ri-user-line text-sm"></i>{result.level}</span>
            </div>

            <a
              href={result.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-3.5 rounded-xl bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 active:scale-[0.98] transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              콘텐츠 보러가기
            </a>
          </div>
        </div>
      ))}

      {/* 기본 이수 프로그램(항상 노출) — 눈에 띄게 강조 + 진입 후 1회 주목 애니메이션 */}
      <a
        href={BASELINE_PROGRAM.link}
        target="_blank"
        rel="noopener noreferrer"
        className="animate-attention relative flex items-center gap-3 rounded-2xl border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-amber-100/70 hover:from-amber-100 hover:to-amber-100 transition-colors px-5 py-4 cursor-pointer shadow-sm"
      >
        <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full bg-amber-500 text-background-50 text-[10px] font-bold shadow-sm">
          필수 · 먼저 이수
        </span>
        <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center shrink-0 shadow-sm">
          <i className="ri-star-smile-fill text-xl text-background-50"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-amber-900">{BASELINE_PROGRAM.title}</p>
          <p className="text-xs text-amber-700 mt-0.5">{BASELINE_PROGRAM.subtitle}</p>
        </div>
        <i className="ri-arrow-right-s-line text-xl text-amber-600"></i>
      </a>

      {/* 하단 액션 */}
      <div className="space-y-2.5 pt-1">
        <button
          type="button"
          onClick={onRestart}
          className="w-full py-3 rounded-xl bg-background-50 text-foreground-500 text-sm font-medium border-2 border-background-200 hover:border-primary-200 hover:text-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          다시 진단하기
        </button>
        <button
          type="button"
          onClick={onGoHome}
          className="w-full py-2.5 text-foreground-400 text-sm font-medium hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          홈으로
        </button>
      </div>
    </div>
  );
}